/**
 * MiniWaveform — Visualizador mini de audio en tiempo real.
 *
 * Muestra barras de frecuencia animadas a partir de un MediaStream.
 * Sirve como confirmación visual de que hay audio entrante activo
 * (ej. la otra persona está hablando durante una llamada WebRTC).
 *
 * Tamaño por defecto: 48×20px (ultra compacto).
 */

import { useRef, useEffect } from 'react';

export interface MiniWaveformProps {
  /** MediaStream a visualizar (típicamente el remoteStream del peer) */
  stream: MediaStream | null;
  /** Color de las barras (CSS color) */
  barColor?: string;
  /** Ancho del canvas en px */
  width?: number;
  /** Alto del canvas en px */
  height?: number;
  /** Clases CSS adicionales */
  className?: string;
}

export function MiniWaveform({
  stream,
  barColor = '#10b981',
  width = 48,
  height = 20,
  className,
}: MiniWaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ctxRef = useRef<{
    audioCtx: AudioContext;
    animFrame: number;
    cloned: MediaStream;
  } | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!stream || !stream.active || !canvas) return;

    // Clonar stream para no interferir con la reproducción principal
    const cloned = stream.clone();
    const audioCtx = new AudioContext();
    const source = audioCtx.createMediaStreamSource(cloned);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 32;
    analyser.smoothingTimeConstant = 0.75;
    source.connect(analyser);

    const canvasCtx = canvas.getContext('2d')!;
    const bufferLength = analyser.frequencyBinCount; // 16 bins
    const dataArray = new Uint8Array(bufferLength);
    const barCount = Math.min(bufferLength, 12);
    const gap = 1;
    const barW = Math.max(2, Math.floor((width - gap * (barCount - 1)) / barCount));

    let animFrame = 0;

    function draw() {
      animFrame = requestAnimationFrame(draw);
      analyser.getByteFrequencyData(dataArray);

      canvasCtx.clearRect(0, 0, width, height);

      for (let i = 0; i < barCount; i++) {
        const val = dataArray[i] / 255;
        const barH = Math.max(2, val * height);
        const x = i * (barW + gap);
        const y = (height - barH) / 2; // centrado vertical
        canvasCtx.fillStyle = barColor;
        canvasCtx.beginPath();
        canvasCtx.roundRect(x, y, barW, barH, 1);
        canvasCtx.fill();
      }
    }
    draw();

    ctxRef.current = { audioCtx, animFrame, cloned };

    return () => {
      cancelAnimationFrame(animFrame);
      cloned.getTracks().forEach((t) => t.stop());
      audioCtx.close().catch(() => {});
      ctxRef.current = null;
    };
  }, [stream, barColor, width, height]);

  if (!stream) return null;

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className={className}
      style={{ display: 'inline-block', verticalAlign: 'middle' }}
      aria-label="Audio entrante"
    />
  );
}
